// THE house keyboard model for menus (arrow cycle, focus-in-on-open,
// focus-back-on-close) — ../../functions/menuKeyboard.js. Held as scope
// VALUES below, never read as bare identifiers from inside a factory: a
// frank.toJSON lift drops those (the FA204 trap this whole file is organised
// around).
import {
  menuArrowNav,
  menuFocusOnOpen,
  menuNodeVisible,
  restoreMenuFocus
} from '../../functions/menuKeyboard.js'
// The ONE resolver for the name the empty state addresses ("Ask anything
// about <workspace>") — ../../functions/assistantScopeName.js. Held as a scope
// VALUE (`scopeName`) for the same frank reason as the menu helpers above.
import { assistantScopeName } from '../../functions/assistantScopeName.js'
import { agentDisplayName } from '../../functions/agentDisplayName.js'

// ── Chat scope (general vs /add-app) ──────────────────────────────────────
// The /add-app builder runs simone in its OWN project-scoped context — one
// extension (= one Project) = one thread. To keep it from colliding with the
// general drawer/composer (which can be open at the same time), its per-context
// chat state lives under `_ax_*` root keys. The host sets `chatScope: 'addapp'`
// on the Chat mount; `ck(el, base)` resolves the right root key by walking up to
// the Chat element that carries the scope. General (default) → the bare keys, so
// this is a no-op until something mounts with chatScope:'addapp'. The session /
// transport functions receive this scope as an explicit arg (the callers below
// resolve it via chatScopeOf) — they never import this module.
export const chatScopeOf = (el) => {
  let n = el
  while (n) {
    if (n.chatScope) return n.chatScope
    n = n.parent
  }
  return 'general'
}
// Self-contained (does NOT reference the sibling `chatScopeOf` helper): frank
// serializes component prop functions and rewrites their free vars to __scope.X,
// but it does NOT recursively rewrite free vars inside hoisted HELPER bodies. So
// a helper that calls another helper (ck → chatScopeOf) throws
// "chatScopeOf is not defined" at runtime in frank/brender consumers (e.g. the
// docs site), aborting the children factory before its reactive effect registers
// → the list never renders. Inlining the scope walk keeps ck free-var-free.
// (See tickets/smbls.md: frank should rewrite hoisted helper bodies too.)
export const ck = (el, base) => {
  let n = el
  let scope = 'general'
  while (n) {
    if (n.chatScope) {
      scope = n.chatScope
      break
    }
    n = n.parent
  }
  return scope === 'addapp' ? '_ax_' + base : scope === 'dashboard' ? '_db_' + base : base
}

// CANONICAL chat surface — the single source of truth for the Simone chat UI.
// Reused VERBATIM (via `extends: 'Chat'` / bare-key mount) by:
//   • standalone simone        (extensions/packages/simone, :1235)
//   • chrome-connect panel      (devtools / side panel)
//   • ext-ui inspector          (extensions/packages/ext-ui ConnectShell, :1236)
//   • workspace AssistantDrawer  (workspace/packages/workspace/app.js)
// Consumers MUST NOT re-implement the header/composer inline — toggle the
// surface flags instead:
//   • root.inspectorTools  → show the composer Inspect $0 button
//   • root.hidePickTool    → hide the composer Pick (cursor-selection) button
//   • root.workspaceEmbed  → workspace-drawer posture: hides the header Me
//                            avatar (the shell chrome already shows one) and
//                            switches Pick copy to "point the AI at an item"
//   • Empty slot override   → ext-ui swaps in its ConnectScreen
//   • Title.Icon.onClick    → ext-ui adds the symbols.app deep-link
export const Chat = {
  // FA204 / frank trap — every helper below (ensureAgentRoster,
  // detectAgentMention, mentionAgents, agentInsertText, resizeComposerTextarea,
  // voiceHostingChat, refreshMentionPicker, pickMentionAgent, send,
  // isComposerSendDisabled, plus DUPLICATE copies of chatScopeOf/ck) used to be
  // bare module-scope const/function read from inside this component's own
  // factories/handlers (onCreate, Header.*, Messages.*, ComposerWrap.*); they
  // read as undefined once this component is frank.toJSON'd for platform
  // delivery. Moved into `scope`, called via `el.scope.<name>` from factories
  // and `this.<name>` between sibling scope methods (object-literal properties
  // can't see each other as bare identifiers). `chatScopeOf`/`ck` ALSO stay
  // exported at module scope (dual-mode, same idiom as AppOrgSwitcher's
  // `__orgSwitcherInternals`) because assistantSimone.js re-exports them bare
  // (`export { chatScopeOf, ck } from './_simone/Chat.js'`) for the published
  // component surface — the scope copies here are what this component's own
  // tree actually calls.
  scope: {
    // ── menu keyboard model (Sessions history dropdown) ──────────────────
    // THE shared implementation (functions/menuKeyboard.js) — the same three
    // helpers the AI model switcher and the composer '+' menu use, so every
    // menu in the assistant answers the same keys.
    navRows: menuArrowNav,
    focusBack: restoreMenuFocus,
    nodeVisible: menuNodeVisible,
    // Plain scope VALUE, and the `when` gate is passed at the CALL SITE: a
    // scope METHOD that called the imported `menuFocusOnOpen` by its bare
    // name would read as undefined after a frank to-json lift
    // (`unresolvable-identifier` — the same trap AiModelSwitcherBody's
    // catalogRows note records). The panel stays MOUNTED and hides with
    // `visibility: hidden` (the AiMenuPanel motion recipe), and a
    // visibility-hidden node cannot take focus — so the open poll must wait
    // for the paint instead of firing once and missing.
    focusOnOpen: menuFocusOnOpen,
    // Plain scope VALUE (see focusOnOpen): the workspace/org name the empty
    // state's Hint and Sub address. Self-contained in its own module, so the
    // lift keeps it whole.
    scopeName: assistantScopeName,

    // The empty state's status line: while this workspace's own starter set
    // is being written, the line that says so; '' otherwise (the list head
    // then shows its micro-label). Reads `root.lang` so a language switch
    // re-runs the factories that call it. Self-contained apart from the
    // `scopeName` VALUE above, reached through `el.scope` like a factory.
    startersStatus(el, s) {
      const root = s && s.root
      void (root && root.lang)
      const name = el.scope.scopeName(root)
      if (!name || !root || !root._aiStartersPending) return ''
      return el.call('polyglot', 'ai.chat.subGenerating').replace('{workspace}', name)
    },

    // ── @-agent mention (composer autocomplete) ──────────────────────────
    // Typing `@name` in the composer opens an agent picker; picking one
    // directs the thread to that agent (root _targetAgentId/_targetAgentName
    // — the existing persona-swap plumbing). Tested spec: ./mention.js (kept
    // in sync).

    // Resolve this Chat instance's scope by walking up for `chatScope` — the
    // same idiom as `ck` below (kept independent so a caller that only needs
    // the scope string doesn't need the base-key logic).
    chatScopeOf(el) {
      let n = el
      while (n) {
        if (n.chatScope) return n.chatScope
        n = n.parent
      }
      return 'general'
    },

    // Self-contained (does NOT call the sibling `chatScopeOf` method): frank
    // serializes component prop functions and rewrites their free vars to
    // __scope.X, but it does NOT recursively rewrite free vars inside hoisted
    // HELPER bodies, so a helper calling another helper across that boundary
    // used to throw at runtime in frank/brender consumers. Inlining the scope
    // walk keeps `ck` self-contained regardless. (See tickets/smbls.md: frank
    // should rewrite hoisted helper bodies too.)
    ck(el, base) {
      let n = el
      let scope = 'general'
      while (n) {
        if (n.chatScope) {
          scope = n.chatScope
          break
        }
        n = n.parent
      }
      return scope === 'addapp' ? '_ax_' + base : scope === 'dashboard' ? '_db_' + base : base
    },

    // Detect an @-mention token in `value` at `caret`. Returns
    // {query,start,end}|null.
    detectAgentMention(value, caret) {
      if (caret == null || !value) return null
      const upto = value.slice(0, caret)
      const at = upto.lastIndexOf('@')
      if (at < 0) return null
      if (at > 0 && !/\s/.test(value[at - 1])) return null
      const after = upto.slice(at + 1)
      if (!/^[a-zA-Z0-9._\- ]*$/.test(after)) return null
      return { query: after, start: at, end: caret }
    },

    // An agent's name in the active interface language (core-wl's rule,
    // workspace 075f2e7e5: the record's metadata.nameI18n for the language,
    // else its own name) — the shared helper held as a scope VALUE and reached
    // through `this.` (FA204: a bare imported identifier would not survive a
    // frank lift).
    agentDisplayName,
    agentLabel(a, lang) {
      return this.agentDisplayName(
        { name: (a && a.name) || '', metadata: { nameI18n: a && a.nameI18n } },
        lang
      )
    },

    // Addressable agents on root (from _agents + people, deduped), filtered by
    // query — the record name, the name shown in `lang`, or the handle.
    mentionAgents(root, query, lang) {
      const seen = new Set()
      const roster = []
      for (const a of [...((root && root._agents) || []), ...((root && root.people) || [])]) {
        if (!a || !a.userId || !(a.kind === 'agent' || a.isAgent)) continue
        const key = String(a.userId)
        if (seen.has(key)) continue
        seen.add(key)
        roster.push(a)
      }
      const q = String(query || '')
        .trim()
        .toLowerCase()
      const matched = q
        ? roster.filter((a) => {
            const name = String(a.name || '').toLowerCase()
            const shown = String(this.agentLabel(a, lang) || '').toLowerCase()
            const handle = String((a.email || '').split('@')[0] || '').toLowerCase()
            return name.includes(q) || shown.includes(q) || handle.includes(q)
          })
        : roster
      return matched.slice(0, 8)
    },

    // The @handle to insert for a picked agent.
    agentInsertText(agent) {
      if (!agent) return ''
      const handle = (agent.email || '').split('@')[0] || agent.name
      return handle ? `@${handle} ` : ''
    },

    // Recompute the composer's @-mention picker from the live textarea + draft.
    // Writes root._simoneAgentPicker (or clears it).
    refreshMentionPicker(el, s) {
      // Ensure the roster is (being) loaded — covers the case where the
      // composer mounted before the SDK attached to the root element. Idempotent.
      this.ensureAgentRoster(el, s)
      const node = el.node
      const value = (node && node.value) || s.draft || ''
      const caret = node && node.selectionStart != null ? node.selectionStart : value.length
      const trig = this.detectAgentMention(value, caret)
      const options = trig ? this.mentionAgents(s.root, trig.query, el.call('getActiveLang')) : []
      if (!trig || !options.length) {
        if (s.root._simoneAgentPicker) s.root.update({ _simoneAgentPicker: null })
        return null
      }
      const picker = {
        query: trig.query,
        start: trig.start,
        end: trig.end,
        options,
        activeIndex: 0
      }
      s.root.update({ _simoneAgentPicker: picker })
      return picker
    },

    // THIS composer's draft state (PromptTextarea's own state bag) from any
    // element inside the composer band — the picker's rows live beside the
    // composer box, not inside it. null outside a composer.
    mentionDraftState(el) {
      const wrap = el && typeof el.lookup === 'function' ? el.lookup('ComposerWrap') : null
      const box =
        wrap && typeof wrap.lookdown === 'function' ? wrap.lookdown('PromptTextarea') : null
      return (box && box.state) || null
    },

    // Commit a picked agent: splice `@handle` into the draft, direct the thread
    // to the agent, close the picker. `s` is the PromptTextarea state (carries draft).
    pickMentionAgent(el, s, agent) {
      const picker = s.root._simoneAgentPicker
      if (!picker || !agent) return
      const insert = this.agentInsertText(agent)
      const draft = s.draft || ''
      s.update({ draft: draft.slice(0, picker.start) + insert + draft.slice(picker.end) })
      s.root.update({
        _simoneAgentPicker: null,
        _targetAgentId: agent.userId,
        // the name the user saw in the picker (the active language)
        _targetAgentName: this.agentLabel(agent, el.call('getActiveLang')) || agent.name
      })
    },

    // Best-effort hydrate of the agent roster for the @-mention picker — the
    // general drawer doesn't mount team-and-agents (which normally fills
    // root._agents). Idempotent via root._agentsLoaded (the SAME guard
    // team-and-agents uses, so they never double-load). Transforms the SDK's
    // Mongo agents to the picker shape inline (tested equivalent:
    // agentsFromMongo in ./mention.js).
    //
    // FALSE-ZERO-1 surface 4: this used to write `_agents` ONLY when the
    // resolved list was non-empty, and swallow the failure path into a bare
    // `root._agentsLoaded = false` reset — so `root._agents === undefined`
    // meant BOTH "this workspace genuinely has zero agents" AND "the load
    // failed", and every consumer ("Your agents" widget, tickets
    // assignables) rendered that ambiguity as a confident "0". Fixed at the
    // producer: ALWAYS write `_agents` on success (`[]` included — a real,
    // verified zero must still render as zero), and record a failure into
    // the declared `_agentsError` key instead of swallowing it, so a failed
    // load stays distinguishable from a genuine empty roster.
    ensureAgentRoster(el, s) {
      const root = s && s.root
      if (!root || root._agentsLoaded) return
      // The SDK lives on the ROOT element (el.sdk isn't set on the composer).
      const rootEl = el && el.getRoot && el.getRoot()
      const sdk = (rootEl && rootEl.sdk) || (el && el.sdk)
      const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
      if (!sdk || typeof sdk.listAgents !== 'function' || !orgId) return
      root._agentsLoaded = true
      Promise.resolve(sdk.listAgents(orgId))
        .then((raw) => {
          const list = Array.isArray(raw) ? raw : (raw && raw.data) || []
          const agents = list
            .map((u) => ({
              id: String(u.username || u.id || u._id || '').toLowerCase(),
              name: u.name || u.username || 'Agent',
              kind: 'agent',
              avatar: u.avatar || null,
              userId: u.id || u._id || null,
              email: u.email || null,
              // the per-language names of a server-seeded agent (agentLabel)
              nameI18n: (u.metadata && u.metadata.nameI18n) || null
            }))
            .filter((a) => a.userId)
          // Always write — [] is a real, verified zero, not a dropped read.
          root.update({ _agents: agents, _agentsError: null })
        })
        .catch((err) => {
          root._agentsLoaded = false
          root.update({ _agentsError: (err && err.message) || 'Failed to load agents' })
        })
    },

    // T18 — is THIS chat composer the live voice host? True while voice is open
    // and root.voiceHost names the chat composer (the VoiceBtn writes it before
    // voicePttPress — full contract in shared/components/VoiceStrip.js; the
    // voiceMinimized axis is deliberately ignored while a composer hosts).
    voiceHostingChat(s) {
      return !!(s && s.root && s.root.voiceOpen && s.root.voiceHost === 'chat')
    },

    isComposerSendDisabled(el, s) {
      return !s.root || s.root.bridgeStatus !== 'open' || !((s.draft || '') + '').trim()
    },

    resizeComposerTextarea(node) {
      if (!node) return
      const hasText = !!node.value
      node.style.whiteSpace = hasText ? 'pre-wrap' : 'nowrap'
      node.style.height = 'auto'
      if (!hasText) {
        node.style.overflowY = 'hidden'
        return
      }
      const maxHeight = window.innerHeight * 0.35
      node.style.height = Math.min(node.scrollHeight, maxHeight) + 'px'
      node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden'
    },

    // Read the PromptTextarea draft from state, hand it to the active transport
    // via `sendPrompt` (carrying this Chat's scope so /add-app dispatches
    // project-scoped), then clear the draft + collapse the auto-grown field.
    // Shared by the Textarea Enter handler and the floating send Button; both
    // inherit PromptTextarea.state, so `s` is that state and `s.update` writes
    // back. No-ops while the bridge is closed.
    // `s.images` is this composer's own attachment bag — UploadBtn below writes
    // into it, and it is the SAME state object the draft lives on. It rides the
    // send as sendPrompt's third argument (workspace/functions/_simone/
    // sendPrompt.js), which normalizes + caps it into the envelope's
    // `attachments`. Cleared with the draft: the turn owns them now.
    send(el, s) {
      const text = ((s.draft || '') + '').trim()
      const images = Array.isArray(s.images) ? s.images : []
      if (!text || s.root.bridgeStatus !== 'open') return
      const scope = this.chatScopeOf(el)
      // THE ASK ROUTER (workspace functions/assistantAskRoute.js
      // assistantAskHandoff): an ask that clearly wants another surface — a
      // page, an app, slides, a website, the tour — goes there; every other
      // ask stays in this chat. Only the general chat routes, and never an ask
      // with images (they ride this chat's own send).
      const fns = el.context && el.context.functions
      const routed =
        scope === 'general' &&
        !images.length &&
        !!fns &&
        typeof fns.assistantAskHandoff === 'function' &&
        !!el.call('assistantAskHandoff', text, { from: 'sidebar' })
      if (!routed) el.call('sendPrompt', text, scope, images)
      s.update({ draft: '', images: [] })
      // setAttribute('value','') only resets the default-value attr — reset the
      // live node and collapse the height the onInput auto-grow left behind.
      const ta = el.node?.tagName === 'TEXTAREA' ? el.node : el.parent?.Textarea?.node
      if (ta) {
        ta.value = ''
        this.resizeComposerTextarea(ta)
      }
    }
  },

  flow: 'y',
  width: '100%',
  height: '100%',
  // In the workspace drawer (root.workspaceEmbed) the chat is a docked
  // SHEET: `common-box`, the surface of the AppDialog panel, so cards and the
  // `card` tooltip step above it — 1.13:1 light, 1.19:1 dark. It painted the
  // page GROUND, `document`, which in light IS the card fill: a tooltip over
  // the drawer read 1.00:1, separated only by its shadow (tooltips lane,
  // 2026-09-24). Standalone (the extension panels) the chat IS the page, so
  // it keeps the ground.
  theme: (el, s) => (s.root && s.root.workspaceEmbed ? 'common-box' : 'document'),
  // Anchors the Settings layer (Chat.Settings → SettingsDrawer, absolute
  // inset 0) to the assistant surface itself — in the docked workspace drawer
  // the settings sheet renders over the sidebar, attached to its trigger,
  // instead of floating viewport-centered over the page content.
  position: 'relative',

  // 'general' (drawer + bottom composer, workspace-scoped) | 'addapp' (the
  // /add-app builder, project-scoped per extension). Set by the host mount;
  // namespaces this Chat's per-context state via el.scope.ck() (see top of file).
  chatScope: 'general',

  // On mount, pull the member's server conversation history so PREVIOUS chats
  // load. The workspace never wired this — hydrateServerSessions only ran after a
  // turn (refreshSessionsAfterTurn), so prior conversations never appeared on
  // open. Runs on the live Chat element (has the SDK); hydrate scopes the SDK to
  // the active workspace itself, so it's timing-safe.
  // …then restore the thread that was OPEN before the page went away
  // (SIMONE-INFLIGHT-TURN-NO-PERSISTENCE-1). activeSessionId/chatMessages are
  // in-memory root state, so every reload — F5, a parcel HMR refresh, a crash,
  // the AppOrgSwitcher org-switch fallback — used to reset the panel to an empty
  // chat even though the server still held the conversation, and a turn that was
  // mid-approval vanished without a trace. `restoreActiveThread` re-reads the
  // remembered thread from the server (never a browser copy of the transcript)
  // and, when the reload killed a live turn, adds one honest notice row. Chained
  // after hydrate so the thread list and the open thread agree; the host that
  // has no such function (standalone simone) just no-ops.
  onCreate: (el) => {
    const scope = el.scope.chatScopeOf(el)
    try {
      const hydrated = el.call('hydrateServerSessions', scope)
      const restore = () => {
        try {
          el.call('restoreActiveThread', scope)
        } catch (_) {
          /* no restore fn (standalone) → panel opens empty, as before */
        }
      }
      if (hydrated && typeof hydrated.then === 'function') hydrated.then(restore, restore)
      else restore()
    } catch (_) {
      /* local / no SDK → keep local sessions */
    }
  },

  // Borderless header. Title (left, flexShrink 0) | spacer | Actions (right,
  // flexShrink 0). Target switching lives in the SettingsDrawer (the header's
  // SettingsBtn — the composer row carries none since 2026-09-26), not in a
  // header pill.
  Header: {
    tag: 'header',
    position: 'relative',
    flow: 'x',
    align: 'center',
    // Compact bar (2026-09-02 per Nika "make sidebar header more compact"):
    // one token step down on every gauge — gap A→Z, padding Z A→Y Z — the
    // same treatment the PageSlot header compaction took. The controls keep
    // their own hit areas; only the bar's outer air shrinks.
    gap: 'Z',
    padding: 'Y Z',
    // A COLOUR name in a THEME slot emits NOTHING — the mirror of the
    // `background: '<theme-name>'` miss. `overlay` is a colour token
    // (designSystem/color.js), not a theme, and `getMediaTheme` on an unknown
    // name returns undefined, so this element rendered with ZERO classes:
    // measured 2026-09-22 against the real pipeline, not a wrong fill — no
    // fill. Reported as "overlays without bg" (Nika, my.symbols.app); the
    // shell mounts this Chat wholesale inside AssistantDrawer. `background:`
    // is the slot that resolves the token, to `var(--color-overlay)`.
    background: 'overlay',
    flexShrink: '0',

    Title: {
      flow: 'x',
      gap: 'Z',
      align: 'center',
      flexShrink: '0',

      // Logo. ext-ui overrides onClick to deep-link the active session into
      // symbols.app; standalone / workspace leave it inert.
      Icon: {
        icon: 'logov2',
        boxSize: 'A1',
        color: 'title'
      },

      // Sessions history dropdown — AppOrgSwitcher-style Trigger + Menu,
      // anchored to the left cluster. Replaces the old toggle button + the
      // separate top-level SessionMenu slot.
      Sessions: {
        position: 'relative',
        flow: 'x',
        align: 'center',

        Trigger: {
          tag: 'button',
          type: 'button',
          flow: 'x',
          gap: 'Z',
          align: 'center',
          padding: 'Y2 Z2',
          round: 'radiusControl',
          theme: 'tertiary',
          color: 'title',
          fontWeight: '500',
          cursor: 'pointer',
          border: 'none',
          background: 'transparent',
          // House trigger states — hover wash at rest, `selected` while the
          // menu is open (the BoardMenu MenuTrigger / AiModelSwitcherTrigger
          // recipe), so every AI menu trigger reads identically.
          ':hover': { background: 'hover' },
          isOpen: (el, s) => !!s.root.showSessionMenu,
          '.isOpen': { background: 'selected' },
          // Menu-button ARIA + keyboard contract (2026-09-02). ArrowDown /
          // ArrowUp open the history menu; Enter and Space need no branch,
          // because this is a real <button> and both already fire onClick.
          ariaHaspopup: 'menu',
          ariaExpanded: (el, s) => (s.root.showSessionMenu ? 'true' : 'false'),
          ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
          onKeydown: (e, el, s) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
            e.preventDefault()
            e.stopPropagation()
            if (!s.root.showSessionMenu) s.root.update({ showSessionMenu: true })
          },
          // The current thread's name. It was cut in JS to 24 characters
          // plus a literal "…" — a cut no language or width could undo, and
          // the name was then nowhere in full. Now the whole name is the
          // text, one line bounded by the `F` measure, and the full name is
          // the `title` (the overflow law, HOME_AMBIENT_SKIN_SPEC §3b rule 8:
          // one-line text keeps its ellipsis only with the whole text there).
          Label: {
            tag: 'span',
            maxWidth: 'F',
            minWidth: '0',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            title: (el, s) => {
              const cur = (s.root.sessions || {})[s.root.activeSessionId]
              return (cur && cur.title) || el.call('polyglot', 'ai.chat.newChat')
            },
            text: (el, s) => {
              const cur = (s.root.sessions || {})[s.root.activeSessionId]
              return (cur && cur.title) || el.call('polyglot', 'ai.chat.newChat')
            }
          },
          // Caret flips while open — static transition on transform only
          // (never on a reactive background — MenuPanel.js note).
          Caret: {
            extends: 'Icon',
            name: 'arrow angle down',
            boxSize: 'Z',
            opacity: '.6',
            transition: 'A defaultBezier',
            transitionProperty: 'transform',
            isOpen: (el, s) => !!s.root.showSessionMenu,
            '.isOpen': { transform: 'rotate(180deg)' }
          },
          onClick: (e, el, s) => {
            e.stopPropagation()
            s.root.update({ showSessionMenu: !s.root.showSessionMenu })
          }
        },

        Menu: {
          // THE shared AiMenuPanel (AiMenu.js): glass surface, radiusCard,
          // borderless, 60vh scroll cap AND the house open/close motion
          // (hidden base + `.isOpen` conditional block, `A defaultBezier`)
          // all come from the base — only the anchor positioning and the
          // isOpen predicate live here, so this panel, the model picker and
          // the composer dropdowns are one shape by construction.
          extends: 'AiMenuPanel',
          isOpen: (el, s) => !!s.root.showSessionMenu,
          position: 'absolute',
          top: '110%',
          left: '0',
          zIndex: '200',

          // Keyboard (2026-09-02): one `role="menu"` over the whole panel, so
          // the arrow cycle runs "New chat" → every session row and wraps —
          // `menuArrowNav` collects `[role="menuitem"]` in DOM order, and the
          // New-chat button carries that role too. Rows are native <button>s,
          // so Enter/Space already activate them. `tabindex: -1` makes the
          // panel a programmatic focus target only (never a tab stop), which
          // is what lets Escape reach this handler when the session list is
          // empty: DOMQL's keydown delegate walks the ancestor chain of
          // `event.target`, so a menu nothing inside it ever focused would
          // never receive the keystroke at all.
          role: 'menu',
          tabindex: '-1',
          ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
          subscribeTo: ['showSessionMenu'],
          onClick: (e) => e.stopPropagation(),
          onKeydown: (e, el, s) => {
            if (el.scope.navRows(e, el.node)) return
            if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              if (s.root && s.root.update) s.root.update({ showSessionMenu: false })
              el.scope.focusBack(el)
              return
            }
            // Tab leaves the menu: close on the way out, but drop the stored
            // return target first so the close edge does not pull focus back
            // to the trigger and fight the Tab.
            if (e.key === 'Tab') {
              el._menuReturnFocusTo = null
              if (s.root && s.root.showSessionMenu) s.root.update({ showSessionMenu: false })
            }
          },
          // iPad: the thread-row context menu (Star / Remove) is unreachable by
          // touch — arm the shared long-press shim at the menu root; the
          // synthetic contextmenu bubbles from the touched row, so one arm
          // covers every session row. Idempotent per node.
          // The focus arm rides the SAME hook (edge-detected inside
          // menuFocusOnOpen, so an unrelated re-render never re-yanks focus).
          onRender: (el, s) => {
            el.call('armLongPress')
            el.scope.focusOnOpen(el, !!s.root.showSessionMenu, {
              when: (node) => el.scope.nodeVisible(node)
            })
          },
          onUpdate: (el, s) =>
            el.scope.focusOnOpen(el, !!s.root.showSessionMenu, {
              when: (node) => el.scope.nodeVisible(node)
            }),
          // Outside-press closes the session menu. FA503: the framework owns
          // this document listener — one registration per element, removed in
          // dispose(), and no `el._docClickGuard` field to keep the two hooks
          // in step.
          onDocumentMousedown: {
            capture: true,
            handler: (ev, el) => {
              const node = el && el.node
              if (!node || node.contains(ev.target)) return
              const root = el.state && el.state.root
              if (root && root.showSessionMenu) root.update({ showSessionMenu: false })
            }
          },

          HeaderRow: {
            flow: 'x',
            align: 'center space-between',
            // Horizontal 0 — the heading self-insets to the rows' Z1 text
            // edge (AiMenuHeading paddingLeft) and the NewBtn's right edge
            // aligns with the option rows' right edge.
            padding: 'Y 0',
            gap: 'Y',
            // THE shared menu caption (AiMenu.js) — one heading treatment
            // across the Sessions menu, model menu and AI settings sections.
            Label: {
              extends: 'AiMenuHeading',
              text: '{{ ai.chat.historyHeading | polyglot }}'
            },
            NewBtn: {
              tag: 'button',
              type: 'button',
              // First stop in the panel's arrow cycle — `menuArrowNav` walks
              // `[role="menuitem"]` in DOM order, and "New chat" is the first
              // actionable control in this panel.
              role: 'menuitem',
              flow: 'x',
              gap: 'X',
              align: 'center',
              padding: 'Y2 Z',
              round: 'radiusControl',
              theme: 'tertiary',
              border: 'none',
              cursor: 'pointer',
              color: 'title',
              fontSize: 'Z1',
              ':hover': { background: 'hover' },
              // Z, not Y — at Y (~6.5px) the plus glyph rendered as a bare
              // dot next to the label (verified live 2026-08-13).
              Plus: { extends: 'Icon', name: 'plus', boxSize: 'Z' },
              Lbl: { tag: 'span', text: '{{ ai.chat.newChat | polyglot }}' },
              onClick: (e, el, s) => {
                e.stopPropagation()
                try {
                  el.call('newSession', el.scope.chatScopeOf(el))
                } catch (_) {}
                if (s.root && s.root.update) s.root.update({ showSessionMenu: false })
              }
            }
          },

          List: {
            flow: 'y',
            gap: 'V',
            children: (el, s) => {
              const sess = s.root.sessions || {}
              return (
                Object.keys(sess)
                  .filter(
                    (id) =>
                      id &&
                      id !== 'null' &&
                      id !== 'undefined' &&
                      sess[id] &&
                      typeof sess[id] === 'object'
                  )
                  .map((id) => sess[id])
                  // Starred (pinned) threads float to the top, then most-recent.
                  .sort((a, b) => {
                    const ap = a.pinned ? 1 : 0
                    const bp = b.pinned ? 1 : 0
                    if (ap !== bp) return bp - ap
                    return (b.ts || 0) - (a.ts || 0)
                  })
              )
            },
            childrenAs: 'state',
            // Session row — the system's row metrics (AiMenu.js AiOptionRow:
            // Y/Z1 padding, radiusControl, 44pt coarse floor) with the
            // session-specific anatomy (star / title / time / ⋮). Active =
            // the house `selected` wash + `title` ink — same recipe as the
            // model + settings option rows. No `transition` on the reactive
            // background (MenuPanel.js note).
            childExtends: {
              tag: 'button',
              type: 'button',
              // Arrow-cycle marker (the panel's own role="menu" walks these);
              // `aria-current` names the thread that is already open, the
              // same fact the `selected` wash carries visually.
              role: 'menuitem',
              ariaCurrent: (el, s) => (s.id === s.root.activeSessionId ? 'true' : null),
              flow: 'x',
              align: 'center space-between',
              width: '100%',
              gap: 'Z',
              padding: 'Y Z1',
              round: 'radiusControl',
              fontSize: 'Z1',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              '@coarse': { minHeight: 'controlH' },
              background: (el, s) => (s.id === s.root.activeSessionId ? 'selected' : 'transparent'),
              color: (el, s) => (s.id === s.root.activeSessionId ? 'title' : 'caption'),
              fontWeight: (el, s) => (s.id === s.root.activeSessionId ? '700' : '500'),
              ':hover': { background: 'hover' },
              // Right-click opens the thread context menu (Star / Remove) at the
              // cursor — the same shell ContextDropdown the /files menu uses.
              onContextmenu: (e, el, s) => {
                e.preventDefault()
                e.stopPropagation()
                if (s.root && s.root.update) {
                  s.root.update({
                    contextDropdown: {
                      open: true,
                      x: e.clientX,
                      y: e.clientY,
                      // lift above the assistant drawer (z-index 10000)
                      z: '20050',
                      sessionId: s.id,
                      itemsFn: 'sessionContextItems',
                      actionFn: 'sessionContextAction'
                    }
                  })
                }
              },
              // ★ marks a starred (pinned) thread; pinned rows also sort to top.
              // (key-based auto-extend: `Icon` extends the Icon built-in.)
              Icon: {
                if: (el, s) => !!s.pinned,
                name: 'star',
                boxSize: 'Z',
                color: 'orange',
                flexShrink: '0'
              },
              // The thread's name — primary text, never cut (overflow law
              // §3b rule 8): it wraps; the time keeps its place.
              Title: {
                text: (el, s) => s.title || el.call('polyglot', 'ai.chat.newChat'),
                overflowWrap: 'anywhere',
                flex: '1 1 auto',
                minWidth: '0'
              },
              Time: {
                text: (el, s) => {
                  if (!s.ts) return ''
                  const d = new Date(s.ts)
                  return el.call('localizedDate', d, { hour: 'numeric', minute: '2-digit' })
                },
                color: 'caption',
                flexShrink: '0'
              },
              // ⋮ opens the same Star / Remove context menu, anchored to the
              // button (replaces the old inline × delete). `Icon_1` auto-extends
              // the Icon built-in (the `_1` suffix is ignored for resolution).
              Icon_1: {
                name: 'three dots vertical',
                boxSize: 'Z',
                color: 'caption',
                cursor: 'pointer',
                flexShrink: '0',
                ':hover': { color: 'title' },
                onClick: (e, el, s) => {
                  e.stopPropagation()
                  const r = (el.node &&
                    el.node.getBoundingClientRect &&
                    el.node.getBoundingClientRect()) || { right: 0, bottom: 0 }
                  if (s.root && s.root.update) {
                    s.root.update({
                      contextDropdown: {
                        open: true,
                        x: Math.round((r.right || 0) - 150),
                        y: Math.round((r.bottom || 0) + 4),
                        // lift above the assistant drawer (z-index 10000)
                        z: '20050',
                        sessionId: s.id,
                        itemsFn: 'sessionContextItems',
                        actionFn: 'sessionContextAction'
                      }
                    })
                  }
                }
              },
              onClick: (e, el, s) => {
                e.stopPropagation()
                try {
                  el.call('switchSession', s.id, el.scope.chatScopeOf(el))
                } catch (_) {}
                if (s.root && s.root.update) s.root.update({ showSessionMenu: false })
              }
            },
            Empty: {
              if: (el, s) =>
                !Object.keys(s.root.sessions || {}).filter(
                  (id) => id && id !== 'null' && id !== 'undefined'
                ).length,
              padding: 'Z A',
              text: '{{ ai.chat.noPrevious | polyglot }}',
              color: 'caption',
              textAlign: 'center'
            }
          }
        }
      }
    },

    // Center spacer — Title pins left, Actions pin right. The old center
    // TargetSwitcher pill was removed (target switching lives in the
    // settings panel — header SettingsBtn → SettingsDrawer TargetSection).
    PvHeaderSpacer: { flex: '1' },

    // Right: Avatar (signed in) + AI ProviderDropdown.
    Actions: {
      flow: 'x',
      gap: 'X',
      align: 'center',
      flexShrink: '0',

      // THE AI-settings entry of the assistant — the shared AiSettingsButton
      // (one implementation with the canvas prompt's ParamsBtn, 2026-08-12
      // per Nika). In a Chat surface it opens the SettingsDrawer → shared
      // AiSettingsPanel. Since 2026-09-26 it is the ONLY one: the composer
      // row carries none (Nika: "remove AI settings button from ai sidebar
      // textarea").
      SettingsBtn: {
        extends: 'AiSettingsButton'
      },

      Me: {
        extends: 'Avatar',
        avatarType: 'initials',
        boxSize: 'B',
        cursor: 'pointer',
        // Hidden in the workspace embed — the shell chrome already shows the
        // signed-in avatar; repeating it inside the drawer is noise.
        hide: (el, s) => !s.root.authUser || !!s.root.workspaceEmbed,
        // Never `''` — an empty `src` is a REAL request for the document URL,
        // and this avatar is mounted (then hidden) in every host with no
        // signed-in user, the preview assistant included. With no user it
        // asks for nothing. A signed-in user with NO photo gets the house
        // initials image: the framework Avatar paints its own initials ONLY
        // from `onError`, so an <img> with no `src` would show the browser's
        // broken-image glyph. See shared/__tests__/emptyImageSrcShared.test.js.
        src: (el, s) => {
          const u = s.root.authUser
          if (!u) return undefined
          return u.avatar || u.avatarUrl || el.call('avatarUrl', u.name || u.email) || undefined
        },
        alt: (el, s) => (s.root.authUser && (s.root.authUser.name || s.root.authUser.email)) || '',
        ':hover': { filter: 'brightness(1.15)' }
      },

      // Local mode: the simone-bridge agent (Claude/Codex) ignores modelMode,
      // so swap the model picker for a live connection badge — a status dot +
      // the active agent name (or connecting… / disconnected). ConnectionBadge
      // reads s.root.bridgeStatus + s.root.agent.
      ConnectionBadge: {
        if: (el, s) => (s.root.targetMode || 'general') === 'local'
      },

      // Model-mode picker — THE shared AiModelSwitcher (one implementation
      // with the canvas prompt bar's ModelBtn; see
      // shared/components/AiModelSwitcher.js). Forces a provider mode on the
      // /agent/conversation request body (default 'auto'), mirroring the
      // server AgentModelModes enum; selection persists to localStorage
      // `symbols_ai_model_mode` (the key sdk.ai.getModelMode reads) so the
      // next send carries body.modelMode. Shown in local mode too
      // (2026-09-02): the switcher body now carries the Provider group with
      // the Local-agent row — it is the one exit from local mode, so hiding
      // it here would trap the user there (the old local-mode `if:` gate
      // predates the move).
      AiModelSwitcher: {}
    }
  },

  // AIUX-SCROLL root cause: the previous onUpdate gated the re-scroll behind
  // a hand-picked content "signature" (`list.length` + the last message's
  // text/resultText length). A turn that only grew a tool-activity card
  // (Stack.Activity → ChatToolRow, or a Stack.Cards row) never touched
  // text/resultText, so the signature stayed identical, onUpdate bailed
  // before ever re-measuring scroll position, and the view stayed pinned
  // wherever it was — reading as a stalled turn (reproduced 3× on the "✓
  // Reading projects 0.3s" tool row). Rebuilt on the SAME ResizeObserver
  // recipe `chat/components/MessageList.js` (ChatMessageList) already uses
  // for its scroll container: it is KEYING-AGNOSTIC — it fires on ANY height
  // change to the message stack regardless of *why* it grew, so it can't
  // miss a growth path the way a hand-picked signature always will. Children
  // moved to a measurable `Inner` stack (mirrors that file's `List` child)
  // since ResizeObserver needs a content box to watch, not the scroll
  // container's own (fixed) box.
  //
  // A TRANSCRIPT, NOT A LANDMARK (DESIGN-SHARED-CHAT-MESSAGES-MAIN-LANDMARK-1):
  // the shell owns the page's one `main`, and this list mounts in the
  // assistant drawer, the /add-app builder and the home's Build tab. As a
  // `<main>` it put a chat forward as the page's main region — the only one
  // on the phone Build tab, a second one with the drawer open over /add-app.
  // It is a plain block with the role a message stream takes: a polite `log`,
  // named in the active language. `aria-busy` holds while THIS thread's turn
  // is in flight, so a screen reader announces the finished answer instead of
  // every streamed chunk. Pinned by __tests__/chatMessagesLog.test.js.
  Messages: {
    role: 'log',
    ariaLive: 'polite',
    ariaLabel: (el) => el.call('polyglot', 'ai.chat.messagesLabel'),
    ariaBusy: (el, s) => (s.root && s.root[el.scope.ck(el, 'chatTyping')] ? 'true' : null),
    flex: '1 1 0',
    minHeight: '0',
    padding: 'A',
    overflow: 'auto',
    // Drawer-inner scroller: edge-bounce must not chain to the page behind
    // the assistant drawer (also correct on the full-page simone surface).
    overscrollBehavior: 'contain',

    // Scroll to bottom on first paint — onMounted fires after the node is
    // attached + laid out (unlike onCreate, where the subtree is still
    // detached and scrollHeight reads 0).
    onMounted: (el) => {
      const container = el.node
      if (!container) return
      el._pinned = true
      container.style.scrollBehavior = 'auto'
      container.scrollTop = container.scrollHeight
      container.style.scrollBehavior = ''
    },

    // "Pinned to bottom" tracking + content-height auto-scroll.
    //
    // A first cut of this fix re-derived "is the user near the bottom" by
    // measuring scrollHeight/scrollTop/clientHeight INSIDE the ResizeObserver
    // callback, i.e. AFTER the content had already grown. That works for a
    // small streaming delta (a few px), but breaks for one big jump — e.g. a
    // full non-streaming answer landing at once, or a burst of tool-activity
    // rows arriving together: the post-growth distance-from-bottom is huge
    // (the growth amount itself), so the check reads "user scrolled away"
    // even when they were exactly pinned to the bottom the instant before.
    // Verified live: injecting 40 messages in one state update while
    // scrollTop was 0 (trivially "at bottom" — the two-message thread fit
    // the container) left distanceFromBottom at 5330px post-growth and the
    // view never moved.
    //
    // Fixed by tracking pinned-state from actual user scroll events instead
    // of re-deriving it from post-resize geometry: `el._pinned` reflects
    // whether the container's scroll position was near the bottom the LAST
    // TIME the user (or our own auto-scroll) moved it — independent of how
    // large the next content jump is. The ResizeObserver only reads that
    // flag; it never recomputes "near bottom" from the grown state.
    // FA503: the pinned-tracker is a DECLARED node handler ('scroll' does not
    // bubble, so DOMQL binds it straight to this container's node and drops it
    // in dispose()) — it no longer has to ride `el._scrollHandler` just so
    // `onRemove` can find it again.
    onScroll: (ev, el) => {
      const container = el.node
      if (!container) return
      el._pinned = container.scrollHeight - container.scrollTop - container.clientHeight < 120
    },

    onCreate: (el) => {
      if (!el || !el.node) return
      const container = el.node
      if (el._pinned === undefined) el._pinned = true

      const inner = container.querySelector('[data-key="Inner"]') || container.firstElementChild
      if (typeof ResizeObserver === 'undefined' || !inner) return
      el._roBottom = new ResizeObserver(() => {
        if (!container.isConnected || !el._pinned) return
        // rAF lets the browser finish the current paint before moving
        // scrollTop — without it the new content sometimes lands half-clipped.
        requestAnimationFrame(() => {
          if (!container.isConnected) return
          container.style.scrollBehavior = 'auto'
          container.scrollTop = container.scrollHeight
          container.style.scrollBehavior = ''
        })
      })
      el._roBottom.observe(inner)
    },

    onRemove: (el) => {
      if (el?._roBottom) {
        try {
          el._roBottom.disconnect()
        } catch (_) {}
        el._roBottom = null
      }
    },

    // Sending a message is a deliberate user action — always bring it into
    // view AND re-pin to the bottom, even if the user had scrolled far up
    // reading history (the pinned-tracking above deliberately does NOT
    // force-scroll an unpinned reader for a background update). Tracks the
    // chatMessages length; a growth whose newest row is the user's OWN
    // message bypasses the pinned check.
    onUpdate: (el, s) => {
      if (!el || !el.node) return
      const list = s.root[el.scope.ck(el, 'chatMessages')] || []
      const prevLen = el._prevLen
      el._prevLen = list.length
      if (prevLen === undefined || list.length <= prevLen) return
      const last = list[list.length - 1]
      if (!last || last.role !== 'user') return
      el._pinned = true
      const container = el.node
      requestAnimationFrame(() => {
        if (!container.isConnected) return
        container.style.scrollBehavior = 'auto'
        container.scrollTop = container.scrollHeight
        container.style.scrollBehavior = ''
      })
    },

    Inner: {
      flow: 'y',
      gap: 'A',
      childExtends: 'ChatMessage',
      // Tag each message that continues a same-author run with `_grouped`, so
      // ChatMessage drops the repeated avatar + Author/Time header and pulls
      // the row up tight (consecutive Claude replies read as one stacked
      // block). Only continuation rows get a fresh object; the first of each
      // group keeps its identity to minimise re-render churn during streaming.
      children: (el, s) => {
        const list = s.root[el.scope.ck(el, 'chatMessages')] || []
        let prevRole = null
        return list.map((m, i) => {
          const grouped = m.role === prevRole
          prevRole = m.role
          // `_last` marks the newest message so its follow-up Suggestions row
          // (ChatMessage) reads at full strength — older completed rows keep
          // their CTAs, stepped back (2026-08-12 per Nika "always followup
          // with beautiful buttons as call to actions from each message").
          // Stamp BOTH the last row (true) and the row before it (false):
          // childrenAs:'state' updates MERGE, so the previous holder must be
          // overwritten explicitly or its stale `_last: true` survives — while
          // untouched older rows keep their identity (the streaming-churn
          // concern above).
          const nearLast = i >= list.length - 2
          if (!grouped && !nearLast) return m
          return {
            ...m,
            ...(grouped ? { _grouped: true } : {}),
            ...(nearLast ? { _last: i === list.length - 1 } : {})
          }
        })
      },
      childrenAs: 'state'
    }
  },

  Empty: {
    if: (el, s) => !(s.root[el.scope.ck(el, 'chatMessages')] || []).length,
    position: 'absolute',
    inset: 'D 0 G 0',
    flow: 'y',
    // Top-justified, not vertically centred (design consult 2026-09-10): the
    // built rows and the generated rows differ in height, and a centred
    // column would move the Hint and the Sub when the generated set lands.
    // Justified to the top, the list grows in one direction only (Rule 66).
    align: 'center flex-start',
    gap: 'Z',
    padding: '0 C',
    // Six two-line rows can outgrow the box this slot reserves above the
    // composer on a short viewport; they scroll inside it rather than show
    // through the composer's overlay wash.
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    // Container stays click-through so it never blocks the composer behind it;
    // the Starters list re-enables pointer events on itself for the rows.
    pointerEvents: 'none',

    // First use of the assistant in a workspace. The workspace drawer is
    // if-latched on `assistantOpen`, so this slot is CREATED at the first
    // open — the moment the workspace package makes sure THIS workspace has
    // its generated starter set (workspace/functions/assistantStarters.js:
    // cache first, one model call only when the set is stale, cached in the
    // member's preferences row). Deferred a microtask so the state writes it
    // makes never land inside this element's own creation pass. Hosts without
    // the function (standalone simone, ext-ui) no-op.
    onCreate: (el, s) => {
      const root = s.root
      queueMicrotask(() => {
        try {
          el.call('ensureWorkspaceStarters', root)
        } catch (_) {}
      })
    },

    // The assistant's mark, above the greeting — a solid-ink tile (the
    // brand's poster moment inside the chrome: one focal point, no hue), so
    // the empty state reads as a welcome rather than a list that starts at
    // the top edge.
    Mark: {
      // a div, never the key's <mark> (the user agent paints it yellow)
      tag: 'div',
      boxSize: 'avatarMd',
      minWidth: 'avatarMd',
      round: 'radiusCard',
      align: 'center center',
      theme: 'primary',
      flexShrink: '0',
      marginBottom: 'Y',
      Icon: { name: 'logov2', boxSize: 'iconLg' }
    },

    Hint: {
      // "Ask anything about <workspace>" (2026-09-10 per Nika). The name is
      // the ONE resolver held in scope as `scopeName` (functions/
      // assistantScopeName.js): an org's default workspace ("Default") is
      // addressed by its org's name. The generic Symbols line survives only
      // where no workspace resolves (standalone simone, pre-boot). `root.lang`
      // is read so a language switch re-runs this factory — el.call('polyglot')
      // captures its value at evaluation time, unlike the template form.
      text: (el, s) => {
        void (s.root && s.root.lang)
        const name = el.scope.scopeName(s.root)
        return name
          ? el.call('polyglot', 'ai.chat.hintWorkspace').replace('{workspace}', name)
          : el.call('polyglot', 'ai.chat.hint')
      },
      color: 'title',
      fontSize: 'B',
      fontWeight: '700',
      textAlign: 'center'
    },
    // THE LIST'S HEAD — one slot above the starters with two faces (Nika
    // 2026-09-26, "make first recommendations buttons in the middle and make
    // better design to it"): the micro-label that names the list ("Try
    // asking") at rest, the status line while this workspace's own set is
    // being written ("Looking at what <workspace> is working on…"). Both
    // live in ONE grid cell and only the current one is visible, so the
    // capsules below never move when one face replaces the other (Rule 66).
    // It replaces the old sentence under the greeting ("Not sure where to
    // start? Try one of these:"), which the label now says in two words.
    Sub: {
      tag: 'div',
      display: 'grid',
      justifyItems: 'center',
      marginTop: 'Z',
      // Both faces re-run on the keys they read (a language switch, the
      // generated set starting or landing, a workspace switch) — declared, so
      // a first run that short-circuits never leaves them untracked (FA515).
      childProps: {
        gridArea: '1 / 1',
        subscribeTo: ['lang', '_aiStartersPending', 'workspace', 'org']
      },
      StartersLabel: {
        extends: 'AppMicroLabel',
        text: (el, s) => {
          void (s.root && s.root.lang)
          return el.call('polyglot', 'ai.chat.startersLabel')
        },
        visibility: (el, s) => (el.scope.startersStatus(el, s) ? 'hidden' : 'visible')
      },
      StartersStatus: {
        tag: 'span',
        color: 'caption',
        fontSize: 'Z1',
        textAlign: 'center',
        maxWidth: 'I',
        text: (el, s) => el.scope.startersStatus(el, s),
        visibility: (el, s) => (el.scope.startersStatus(el, s) ? 'visible' : 'hidden')
      }
    },

    // Starter prompts — click to ask the AI. Each sends straight through the
    // active transport via el.call('sendPrompt', …) — identical to typing the
    // prompt and pressing Enter in the composer (bridge in standalone simone,
    // sdk.ai in the workspace drawer).
    //
    // PER WORKSPACE, never a brochure (2026-09-10 per Nika "make those options
    // smart and more beautiful per workspace — not static or generic —
    // generated on workspace use"). The rows come from the workspace
    // package's `workspaceStarters(root)`: this workspace's GENERATED set when
    // it has a fresh one, else a set BUILT from the apps the workspace
    // actually shows — each row carrying that app's glyph and name
    // (AssistantStarter). A host with no such function (standalone simone)
    // gets the two generic staples.
    //
    // CENTRED CAPSULES OF ONE WIDTH (Nika 2026-09-26, "make first
    // recommendations buttons in the middle and make better design to it").
    // One grid track, `minmax(0, max-content)`: as wide as the longest
    // starter in the active language, never wider than the panel, centred;
    // every capsule stretches to it, so the column reads as one even stack
    // in English and in Georgian (a long prompt wraps inside its capsule
    // instead of widening one of them). The kit's even-width construction:
    // one shared track — the same one the Danger zone's buttons share.
    // THE centred starter list (AssistantStarter.js AssistantStarterList —
    // one track as wide as the longest starter, the compact capsule: six fit
    // the empty state on a short viewport; at 44px the sixth sat under the
    // fold of a 757px window, live on dev 2c2b6d978). The Page view's empty
    // workspace mounts the same list. The rows are this host's own.
    Starters: {
      extends: 'AssistantStarterList',
      marginTop: 'Y',
      pointerEvents: 'auto',
      children: (el, s) => {
        const root = s.root || {}
        // Every input the workspace builder reads, touched here so the list
        // follows a workspace switch, the apps landing, a language switch and
        // the generated set arriving in the preferences row.
        void [
          root.lang,
          root.workspace,
          root.org,
          root.workspaceApps,
          root.workspaceSettings,
          root.workspaceProject,
          root.userPrefs,
          root.assistantOpen,
          root._aiStartersPending
        ]
        let rows = null
        try {
          rows = el.call('workspaceStarters', root)
        } catch (_) {
          rows = null
        }
        if (Array.isArray(rows) && rows.length) {
          // A stale or missing generated set re-arms generation. Idempotent:
          // a fresh cache, an in-flight call and a recent failure all return
          // at once, and the state writes it makes are deferred past this
          // render (queueMicrotask) so an effect never updates mid-effect.
          queueMicrotask(() => {
            try {
              el.call('ensureWorkspaceStarters', root)
            } catch (_) {}
          })
          return rows
        }
        return [
          {
            key: 'generic-help',
            text: el.call('polyglot', 'ai.chat.starter.help'),
            icon: 'magicstar'
          },
          {
            key: 'generic-symbols',
            text: el.call('polyglot', 'ai.chat.starter.symbols'),
            icon: 'magicstar'
          }
        ]
      }
    }
  },

  // Multi-agent panel — the delegated/chained sub-agents working on the current
  // turn (live, from the SDK onProcessActive agent.started/completed events),
  // PLUS the retained history of prior runs (runHistory) so past parallel/chained
  // runs stay reviewable after their turn ends. The transport moves each finished
  // batch from activeProcesses → runHistory (capped 30) instead of dropping it.
  // Sits above the composer; present while there are live OR past runs
  // (scope-aware via ck). Live runs tint blue (running) / green (done) / red
  // (error); past runs are already done/error so they read green/red. Reuses the
  // overlay-card + status-tint look; Pv* keys avoid built-in auto-extend
  // collisions; reactive text/color/background only (padding/round stay static).
  ActiveProcesses: {
    if: (el, s) =>
      (s.root[el.scope.ck(el, 'activeProcesses')] || []).length > 0 ||
      (s.root[el.scope.ck(el, 'runHistory')] || []).length > 0,
    flow: 'y',
    gap: 'Z',
    margin: '- A Z',
    padding: 'Z1',
    round: 'B',
    // `overlay` is a COLOUR token, never a theme — see Header above. In the
    // theme slot it emitted no class at all, so this card had no plate.
    background: 'overlay',
    flexShrink: '0',

    PvHead: {
      flow: 'x',
      align: 'center left',
      gap: 'Z',
      PvLabel: {
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'caption',
        text: (el, s) => {
          const running = (s.root[el.scope.ck(el, 'activeProcesses')] || []).some(
            (p) => p.status === 'running'
          )
          return running ? 'Agents working' : 'Recent agent runs'
        }
      },
      PvCount: {
        marginLeft: 'auto',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          const active = s.root[el.scope.ck(el, 'activeProcesses')] || []
          const history = s.root[el.scope.ck(el, 'runHistory')] || []
          const running = active.filter((p) => p.status === 'running').length
          return running ? `${running} running` : `${active.length + history.length} runs`
        }
      },
      // Dismiss the whole panel — clears the retained history and any finished
      // (non-running) current batch. Hidden mid-turn (never yank a live run) and
      // when there's nothing to clear.
      PvClear: {
        tag: 'button',
        type: 'button',
        marginLeft: 'Y',
        padding: '0',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 'Y2',
        color: 'caption',
        ':hover': { color: 'title' },
        text: '{{ ai.chat.clear | polyglot }}',
        hide: (el, s) => {
          const active = s.root[el.scope.ck(el, 'activeProcesses')] || []
          const history = s.root[el.scope.ck(el, 'runHistory')] || []
          const running = active.some((p) => p.status === 'running')
          return running || (!active.length && !history.length)
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          s.root.update({
            [el.scope.ck(el, 'activeProcesses')]: [],
            [el.scope.ck(el, 'runHistory')]: []
          })
        }
      }
    },

    PvRows: {
      flow: 'y',
      gap: 'Y',
      childrenAs: 'state',
      // Live runs first (current turn), then the retained history (capped 6) so
      // the panel reads "working now → earlier". Past rows carry _past for a
      // subtle dim; ids never overlap (history is only populated once a batch has
      // left activeProcesses).
      children: (el, s) => {
        const active = s.root[el.scope.ck(el, 'activeProcesses')] || []
        const history = (s.root[el.scope.ck(el, 'runHistory')] || []).slice(0, 6)
        return [...active, ...history.map((h) => ({ ...h, _past: true }))]
      },
      childExtends: {
        flow: 'x',
        gap: 'Z',
        align: 'center left',
        padding: 'Y Z',
        round: 'X2',
        opacity: (el, s) => (s._past ? '0.6' : '1'),
        background: (el, s) =>
          s.status === 'error' ? 'red.15' : s.status === 'done' ? 'green.15' : 'blue.15',
        PvInfo: {
          flow: 'y',
          flex: '1 1 0',
          minWidth: '0',
          PvName: {
            fontSize: 'Z1',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => s.agentName || s.seedSlug || 'Agent'
          },
          PvTask: {
            fontSize: 'Y2',
            color: 'caption',
            overflow: 'hidden',
            text: (el, s) => (s.status === 'error' ? s.error || 'failed' : s.task || s.result || '')
          }
        },
        PvStatus: {
          marginLeft: 'auto',
          flexShrink: '0',
          fontSize: 'Y2',
          color: (el, s) => (s.status === 'error' ? 'red' : s.status === 'done' ? 'green' : 'blue'),
          text: (el, s) =>
            s.status === 'error' ? 'error' : s.status === 'done' ? '✓ done' : 'running…'
        }
      }
    }
  },

  // Composer — brand `PromptTextarea` with its marketing Flex/Button stripped
  // and a chat toolbar (Pick/Inspect/Upload/Settings) + circular send pinned
  // inside it. The draft lives on PromptTextarea.state (`draft`); Textarea +
  // CircleButton inherit it, so `_send` reads/clears via state. Disabled until
  // the bridge connects. Pick shows everywhere except the workspace embed
  // (`hidePickTool`); Inspect $0 only shows when `inspectorTools` is set.
  ComposerWrap: {
    tag: 'form',
    padding: 'A',
    paddingBottom: 'max(env(safe-area-inset-bottom), A)',
    borderTop: 'none',
    // `overlay` is a COLOUR token, never a theme — see Header above. In the
    // theme slot it emitted no class at all, so the composer band had no plate.
    background: 'overlay',
    flexShrink: '0',
    flow: 'x',
    align: 'center center',
    // Anchors the "→ Talking to <agent>" chip above the composer.
    position: 'relative',

    // Correspondent chip. It says WHO this panel is talking to, in two cases:
    //   1. the thread BELONGS to an agent (entered from its card —
    //      root._threadAgentName). Clicking it leaves that correspondent and
    //      resumes the general assistant's own thread.
    //   2. an @-mention or an in-thread agent chip has aimed only the NEXT
    //      message at an agent (root._targetAgentName). Clicking it clears
    //      that aim and the thread stays where it is.
    // Case 1 is what makes the panel say which thread it resumed
    // (AI-TALK-TO-AGENT-REUSES-OPEN-THREAD-1 acceptance).
    TargetAgentChip: {
      tag: 'button',
      type: 'button',
      // Bare <button>, so it inherits no TooltipTrigger — a plain `title`
      // here rendered the NATIVE browser tooltip right beside the house
      // ones on the toolbar below. Trigger + tooltipText directly instead;
      // ariaLabel carries what the ✕ means for screen readers.
      extends: 'TooltipTrigger',
      tooltipText: (el, s) =>
        s.root._threadAgentName
          ? 'Leave this agent and go back to the general assistant'
          : 'Stop directing this thread to the agent',
      tooltipPlacement: 'top',
      aria: {
        label: (el, s) =>
          s.root._threadAgentName
            ? 'Leave this agent and go back to the general assistant'
            : 'Stop directing this thread to the agent'
      },
      hide: (el, s) => !(s.root._threadAgentName || s.root._targetAgentName),
      position: 'absolute',
      bottom: 'calc(100% + Z)',
      left: 'A',
      zIndex: '25',
      flow: 'x',
      align: 'center center',
      gap: 'Y',
      padding: 'Z Z1',
      round: 'F',
      borderWidth: '0',
      cursor: 'pointer',
      background: 'overlayStrong',
      color: 'title',
      fontSize: 'Y2',
      Label: {
        tag: 'span',
        text: (el, s) => '→ ' + (s.root._threadAgentName || s.root._targetAgentName || '')
      },
      X: { tag: 'span', text: '✕', color: 'caption', marginLeft: 'Y' },
      onClick: (e, el, s) => {
        // In an agent's OWN thread the chip is that thread's identity, so
        // leaving is a thread move, not a flag clear — go back to the general
        // assistant's thread, which is resumed the same way.
        if (s.root._threadAgentId) {
          el.call('openAgentThread', null, el.scope.chatScopeOf(el))
          return
        }
        s.root.update({ _targetAgentId: null, _targetAgentName: null })
      }
    },

    // @-mention agent picker — floats above the COMPOSER BAND. It is a child
    // of ComposerWrap, never of PromptTextarea: the brand PromptTextarea box
    // is `overflow: hidden`, so a panel that floats ABOVE it was clipped away —
    // present in the DOM, invisible, and a click at its place hit the message
    // list (design-d2's live finding, 2026-09-29). Rows use childrenAs:'state'
    // (per-row data via state — no closure capture, FA205); a pick reaches
    // THIS composer's draft state through the tree (`mentionDraftState`).
    // Shown only while root._simoneAgentPicker has options.
    SimoneMentionDropdown: {
      // Declared (FA515): the voice check below short-circuits, so the picker
      // key is read only on some runs — the dependency is stated, not implied.
      hideDeps: ['_simoneAgentPicker'],
      // Draft-coupled — hides with the textarea while voice hosts (T18).
      hide: (el, s) =>
        el.scope.voiceHostingChat(s) ||
        !(
          s.root._simoneAgentPicker &&
          s.root._simoneAgentPicker.options &&
          s.root._simoneAgentPicker.options.length
        ),
      // Aligned with the composer box inside the band's `A` inset, floating
      // just above the band's top edge.
      position: 'absolute',
      left: 'A',
      right: 'A',
      bottom: 'calc(100% - Y)',
      zIndex: '30',
      flow: 'y',
      gap: 'V',
      // THE AI menu surface (AiMenu.js AiMenuPanel values, restated —
      // this panel is display-gated by the typeahead `hide` above, so it
      // takes the brand entrance keyframe instead of the .isOpen
      // transition: `fadeInUpShort` replays on every reveal (the
      // VoiceStrip precedent for display-gated swaps) and close stays
      // instant — a keystroke-driven picker must never lag the caret.
      // Borderless: the old hairline ring was the banned separation
      // stroke (design-direction.md §8) — the glass theme's own single
      // shadow is the separation.
      padding: 'Z',
      theme: 'canvas-card-glass',
      round: 'radiusCard',
      borderWidth: '0',
      // Overlay elevation token (the AiMenuPanel note): the glass theme's
      // light-half shadow computes to none, and THIS panel floats over the
      // often-empty white chat area — without it the picker was literally
      // white-on-white invisible in light (measured 2026-08-13).
      shadow: 'overlay',
      animation: 'fadeInUpShort',
      animationDuration: 'A',
      maxHeight: '40dvh',
      overflow: 'auto',
      overscrollBehavior: 'contain',
      // The row stays an INLINE shape on purpose: this Chat is reused verbatim
      // by hosts that register only `Chat` (standalone simone, the chrome
      // panel, the ext-ui inspector), where a named row component would not
      // resolve.
      childExtends: {
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center flex-start',
        gap: 'Y',
        width: '100%',
        padding: 'Y Z1',
        round: 'radiusControl',
        borderWidth: '0',
        cursor: 'pointer',
        textAlign: 'left',
        // Active row wears the house active wash; __i is the row's picker index.
        // Active row wears the house active wash (the accepted overlay value).
        // Hover unifies with keyboard nav by moving activeIndex, so a single
        // highlight mechanism serves both (no static rgba :hover).
        background: (el, s) =>
          (s.root._simoneAgentPicker && s.root._simoneAgentPicker.activeIndex) === s.__i
            ? 'selected'
            : 'transparent',
        onMouseenter: (e, el, s) => {
          const p = s.root._simoneAgentPicker
          if (p && p.activeIndex !== s.__i)
            s.root.update({ _simoneAgentPicker: { ...p, activeIndex: s.__i } })
        },
        // onMousedown (not onClick) + preventDefault so the pick fires before
        // the textarea blur, keeping focus + the caret in the input. `s` is the
        // row's agent state; the draft state is THIS composer's PromptTextarea
        // state, found through the tree.
        onMousedown: (e, el, s) => {
          e.preventDefault()
          const draft = el.scope.mentionDraftState(el)
          if (draft) el.scope.pickMentionAgent(el, draft, s)
        },
        Avatar: {
          extends: 'Avatar',
          avatarType: 'initials',
          boxSize: 'C C',
          flexShrink: '0',
          round: '50%',
          src: (el, s) => s.avatar || el.call('avatarUrl', s.name || s.email || '?')
        },
        Meta: {
          flow: 'y',
          minWidth: '0',
          Name: {
            color: 'title',
            fontSize: 'Z1',
            fontWeight: '500',
            text: (el, s) => el.scope.agentLabel(s, el.call('getActiveLang')) || 'Agent'
          },
          Handle: {
            color: 'caption',
            fontSize: 'Y2',
            text: (el, s) =>
              '@' + ((s.email || '').split('@')[0] || String(s.name || '').toLowerCase())
          }
        }
      },
      childrenAs: 'state',
      children: (el, s) =>
        ((s.root._simoneAgentPicker && s.root._simoneAgentPicker.options) || []).map(
          (agent, i) => ({ ...agent, __i: i, key: String(agent.userId) })
        )
    },

    PromptTextarea: {
      width: '100%',
      boxSize: 'auto 100%',
      minHeight: '0',
      height: 'auto',
      maxHeight: '35dvh',
      // Column flex so the awareness bar can sit ABOVE the textarea: brand's
      // PromptTextarea is a block whose `Textarea` key merges first, so the
      // bar (a new key) lands after it in source order — under the textarea,
      // inside the bottom-anchored composer control row (ComposerBar), clipped by
      // this card's overflow:hidden (AI-CONTEXT-AWARENESS-O1-1 live pass,
      // 2026-08-21). `order: -1` on the bar is the declarative fix; the
      // Textarea's own autosize/height rules are untouched.
      flow: 'y',
      // A docked panel's corners (radiusSheet), not the brand hero capsule's
      // `B` — a two-row composer on a capsule radius read as a pill.
      round: 'radiusSheet',
      // Brand's PromptTextarea pins a FIXED height per breakpoint (`boxSize:
      // 'E2 I'` at @tabletM, 'E2 100%' / 'F1 100%' below) — those media rules
      // outrank the flat `height: auto` above, so at ≤tabletM widths the card
      // stayed E2 tall, the bar + textarea overflowed it and overflow:hidden
      // clipped the textarea (live at 1000px, 2026-08-21). The chat composer
      // is content-sized at every width.
      '@tabletM': { boxSize: 'auto 100%' },
      '@tabletS': { boxSize: 'auto 100%' },
      '@mobileL': { boxSize: 'auto 100%' },
      outline: 'none',
      boxShadow: 'none',
      borderColor: 'transparent',
      // The BAR is the visual field (the textarea inside it suppresses its
      // own ring below), so THE house ring lands here on :focus-within —
      // `focusRing` shadow token, DESIGN_LANGUAGE §8.2. This used to kill the
      // ring outright (`boxShadow: 'none'`), leaving Simone's composer the
      // one form element with NO keyboard focus state (F1, Nika 2026-08-16).
      ':focus-within': { outline: 'none', shadow: 'focusRing', borderColor: 'transparent' },
      // position:relative anchors this box's own absolute parts. (The
      // @-mention picker floats from ComposerWrap, OUTSIDE this box — its
      // overflow is hidden and would clip it.)
      position: 'relative',
      // `images` MUST be declared here, not left to appear on first upload.
      // UploadBtn below walks up the tree until it finds a state whose
      // `images` IS AN ARRAY — with the key undeclared, that walk ran past
      // this composer all the way to the root and wrote the picture onto the
      // ROOT state, where `send` (which reads THIS state) could only see it by
      // accident of state inheritance, and two live composers would share one
      // bag. Declaring it stops the walk here, which is what makes the
      // attachment belong to this composer.
      state: { draft: '', images: [] },

      // ── Ref-drop target (spec §4.1) ──────────────────────────────────────
      // The drawer composer accepts an entity drag exactly like the chat
      // Composer does (packages/chat/components/Composer.js) — same declared
      // attribute, same three handlers, same dragRef.js wire contract reached
      // through el.call so no package imports another (FA001).
      //
      // WHERE IT DIFFERS FROM CHAT: chat splices a markdown LINK into the
      // message body, because a chat message has nowhere else to put a ref.
      // The assistant does: a dropped ref becomes a STRUCTURED attachment
      // (`el.call('attachAiRef', ref)` → root.aiAttached), so it reaches the
      // model as data the server keeps and the orchestrator registers as a
      // retrieval candidate — not as text in the prompt. AiContextBar above
      // renders the chip and owns the remove.
      //
      // `data-ref-drop` is what the POINTER-drag sources hit-test on pointerup
      // (the tickets board — dragRef.js dispatchRefDrop delivers through this
      // very onDrop via a synthetic DragEvent). Declaring it is the whole
      // opt-in; there is no second registration.
      'data-ref-drop': 'assistant',
      // The drop affordance is an INSTANCE flag + an imperative el.update()
      // patch, never root state: root.update() silently drops any key the
      // shell's state schema does not declare, and a hover ring is not state
      // worth declaring. Same technique the chat Composer uses. The wash is
      // the house `selected` step — the composer already owns a focus ring
      // (:focus-within shadow above) and a second ring would be two rings.
      _dragOverOn: { background: 'selected' },
      _dragOverOff: { background: 'none' },
      onDragover: (e, el) => {
        if (!el.call('hasDragRef', e)) return
        // MUST preventDefault, or the browser shows the "can't drop here"
        // cursor for our custom MIME. getData() is blocked during dragover
        // (DataTransfer protected mode), so hasDragRef tests `.types` only.
        e.preventDefault()
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
        if (el._dragOver) return
        el._dragOver = true
        el.update(el._dragOverOn)
      },
      onDragleave: (e, el) => {
        // dragleave also fires when the pointer crosses onto a DESCENDANT
        // (the textarea, the toolbar, a chip) — only clear once it has really
        // left this element's box. relatedTarget is null when the drag leaves
        // the window, and the guard then falls through and clears correctly.
        if (el.node && e.relatedTarget && el.node.contains(e.relatedTarget)) return
        if (!el._dragOver) return
        el._dragOver = false
        el.update(el._dragOverOff)
      },
      onDrop: (e, el) => {
        if (el._dragOver) {
          el._dragOver = false
          el.update(el._dragOverOff)
        }
        const ref = el.call('readDragRef', e)
        if (!ref) return
        e.preventDefault()
        try {
          // drag-ref `kind` === EntityRef `type` (spec §2) — attachAiRef
          // accepts either spelling, so the payload goes through untouched.
          el.call('attachAiRef', ref)
        } catch (_) {
          // Unregistered on this surface (standalone simone, the chrome
          // panel) — the drop is a no-op instead of a throw out of a handler.
        }
      },

      // Brand PromptTextarea ships an upload/chevron Flex + a marketing
      // "Create a feature" Button — neither belongs in the chat composer.
      // O1 (opus.md): the ONE awareness rendering, INSIDE PromptTextarea so its
      // inherited state is this composer's own draft bag (images live here) —
      // that is the `composerState` the resolver takes as its second argument.
      // Replaces the top-level PickedContextChip mount: picked elements are the
      // "attached" tier now, next to the focus tier (open record, canvas
      // selection) — one grammar, and the `sent` truth on every chip.
      AiContextBar: {
        order: '-1',
        flexShrink: '0',
        padding: 'Z1 A2 0'
      },
      Flex: { if: () => false },
      Button: { if: () => false },

      Textarea: {
        // Hidden (not unmounted) while the voice strip hosts this composer
        // (T18) — reactive `hide` on a mounted-open child.
        hide: (el, s) => el.scope.voiceHostingChat(s),
        rows: '1',
        resize: 'none',
        border: 'none',
        fontSize: 'inherit',
        // A typed draft in the title ink: the field's inherited text ink
        // (rgba(0,0,0,.65)) read like its placeholder (rgb(85,85,93)). The
        // placeholder keeps its own rule.
        color: 'title',
        minWidth: '100%',
        width: '100%',
        minHeight: '1.5em',
        height: 'auto',
        maxHeight: '35dvh',
        overflow: 'hidden',
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        // Tokens only. The old raw right inset and the C bottom inset reserved
        // room for ABSOLUTELY placed buttons; the controls now sit in a flow
        // row below (ComposerBar), so the text keeps the field's own inset.
        padding: 'A2 A2 Y A2',
        outline: 'none',
        boxShadow: 'none',
        ':focus': { outline: 'none', boxShadow: 'none' },
        ':focus-visible': { outline: 'none', boxShadow: 'none' },
        // AIUX-CHROME item 2 (tickets/sonnet.md, live-verified 2026-08-13) —
        // the static "…or reference $0…" hint is a Chrome-DevTools-style
        // convention (Inspect $0 — the InspectZeroBtn tooltip below) that
        // only means anything to the chrome-extension inspector surface
        // (`root.inspectorTools`); every other consumer (workspace drawer,
        // dashboard, standalone) showed it too, reading as a stray price/bug
        // token to new users who have no "$0" feature visible at all. Only
        // surface the hint where it's actually true.
        // Through the bundle (it was an English literal in every workspace
        // language — the i18n gate reads only `prop: 'literal'`, so a literal
        // inside a factory slipped past it).
        placeholder: (el, s) =>
          el.call(
            'polyglot',
            s.root && s.root.inspectorTools ? 'ai.chat.placeholderInspector' : 'ai.chat.placeholder'
          ),
        value: (el, s) => s.draft || '',
        disabled: (el, s) => s.root.bridgeStatus !== 'open',
        onCreate: (el, s) => {
          el.scope.resizeComposerTextarea(el.node)
          // Hydrate the @-mention agent roster (best-effort, idempotent).
          el.scope.ensureAgentRoster(el, s)
          if (typeof ResizeObserver === 'undefined') return
          el._composerResizeObserver = new ResizeObserver(() =>
            el.scope.resizeComposerTextarea(el.node)
          )
          el._composerResizeObserver.observe(el.node)
        },
        onRemove: (el) => {
          el._composerResizeObserver?.disconnect?.()
        },
        onUpdate: (el) => {
          el.scope.resizeComposerTextarea(el.node)
        },
        onInput: (e, el, s) => {
          s.update({ draft: e.target.value })
          el.scope.resizeComposerTextarea(el.node)
          el.scope.refreshMentionPicker(el, s)
        },
        // Enter sends (chat convention); Shift+Enter inserts a newline. When the
        // @-mention picker is open it captures Arrow/Enter/Tab/Escape first.
        onKeydown: (e, el, s) => {
          const picker = s.root._simoneAgentPicker
          if (picker && picker.options && picker.options.length) {
            const n = picker.options.length
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              s.root.update({
                _simoneAgentPicker: { ...picker, activeIndex: (picker.activeIndex + 1) % n }
              })
              return
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault()
              s.root.update({
                _simoneAgentPicker: { ...picker, activeIndex: (picker.activeIndex - 1 + n) % n }
              })
              return
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
              e.preventDefault()
              el.scope.pickMentionAgent(
                el,
                s,
                picker.options[picker.activeIndex] || picker.options[0]
              )
              return
            }
            if (e.key === 'Escape') {
              e.preventDefault()
              s.root.update({ _simoneAgentPicker: null })
              return
            }
          }
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            el.scope.send(el, s)
          }
        }
      },

      // Composer toolbar — NavbarButton icon buttons pinned bottom-left.
      // Pick (cursor selection) + Upload + Settings show everywhere except
      // where a host hides Pick (`hidePickTool` — workspace embed); Inspect $0
      // is inspector-only (chrome extension).
      // The composer's control row — IN FLOW under the text, never absolute.
      // Left: upload / voice / settings; right: send. One centre line, one
      // compact scale (AssistantIconButton + AssistantSendButton), so nothing
      // needs a hand-tuned inset and the text keeps its own padding.
      ComposerBar: {
        hide: (el, s) => el.scope.voiceHostingChat(s),
        flow: 'x',
        align: 'center space-between',
        gap: 'Z',
        padding: '0 Z Z Z',
        flexShrink: '0',
        PromptButtons: {
          flow: 'x',
          align: 'center start',
          gap: 'X',
          flexShrink: '0',
          // Every control here is the assistant chrome's one icon button —
          // the primitive owns the scale and the states (Rules 65 + 68).
          childExtends: 'AssistantIconButton',

          PickBtn: {
            if: () => false,
            icon: 'sf cursor',
            // Surface-specific intent: in the chrome extension the picked DOM
            // node is the thing the agent should UPDATE; in the workspace embed
            // (and standalone) picking just POINTS the AI at an item as context.
            title: (el, s) => {
              if (s.root && s.root.picking) return 'Picking… (Esc to cancel)'
              if (typeof window !== 'undefined' && typeof window.__ccSendToPage === 'function') {
                return 'Pick the element to update on the inspected page'
              }
              return s.root && s.root.workspaceEmbed
                ? 'Point the AI at an item on this page'
                : 'Point the AI at an element on this page'
            },
            background: (el, s) => (s.root && s.root.picking ? 'primary' : undefined),
            onClick: (e, el) => {
              try {
                el.call('togglePick')
              } catch (_) {}
            }
          },
          InspectZeroBtn: {
            if: (el, s) => !!(s.root && s.root.inspectorTools),
            icon: 'sf inspect',
            title: (el, s) =>
              typeof window !== 'undefined' && window.__ccSurfaceMode === 'side-panel'
                ? 'Capture the currently-focused element on the active tab'
                : 'Inspect $0 from the Elements panel',
            onClick: (e, el) => {
              try {
                el.call('inspectSelected')
              } catch (_) {}
            }
          },
          UploadBtn: {
            tag: 'label',
            icon: 'sf upload',
            title: (el) => el.call('polyglot', 'ai.chat.uploadImage'),
            position: 'relative',
            cursor: 'pointer',
            Input: {
              tag: 'input',
              type: 'file',
              accept: 'image/*',
              position: 'absolute',
              inset: '0',
              opacity: '0',
              cursor: 'pointer',
              onChange: (e, el) => {
                const file = e.target.files && e.target.files[0]
                if (!file || !file.type.startsWith('image/')) return
                let promptEl = el.parent
                while (
                  promptEl &&
                  promptEl.parent &&
                  !(promptEl.state && Array.isArray(promptEl.state.images))
                ) {
                  promptEl = promptEl.parent
                }
                const st = promptEl && promptEl.state
                const nodeRef = el.node
                const reader = new FileReader()
                reader.onloadend = () => {
                  // The SAME entry shape the canvas composer stores
                  // ({ key, name, mime, size, dataUrl }) — a bare data-URL
                  // string used to be pushed here, which left the awareness chip
                  // with no file name and no `key` to remove it by. `key` is the
                  // handle removePromptAttachment deletes on.
                  const entry = {
                    key: `img-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                    name: file.name || 'image',
                    mime: file.type || '',
                    size: file.size || 0,
                    dataUrl: reader.result
                  }
                  if (st && st.update) st.update({ images: [...(st.images || []), entry] })
                  if (nodeRef) nodeRef.value = ''
                }
                reader.readAsDataURL(file)
              }
            }
          },
          // Voice input — talk to the assistant hands-free. Click toggles the
          // shared VoiceOverlay in talk mode (replies are read aloud), holding
          // (≥280ms) is push-to-talk. Renders only where a VoiceOverlay host is
          // mounted (voiceHostReady — chrome-connect/ext-ui surfaces never show
          // it). Own `scope` isolates the PTT hold timer.
          VoiceBtn: {
            scope: {},
            display: null,
            show: (el, s) => !!(s.root && s.root.voiceHostReady),
            icon: (el, s) => (s.root && s.root.voiceOpen ? 'mic' : 'micOutline'),
            title: (el) => el.call('polyglot', 'ai.chat.voiceTooltip'),
            isActive: (el, s) => !!(s.root && s.root.voiceOpen),
            '.isActive': { background: 'overlayStrong', color: 'title' },
            onPointerdown: (e, el, s) => {
              if (e.button !== 0) return
              // Capture on the BUTTON, not `e.target` — see the twin handler in
              // canvas/components/CanvasPromptTextarea.js for the full rationale:
              // `e.target` is usually the inner icon, `icon` is reactive and
              // swaps the moment `voiceOpen` flips (i.e. as a hold begins), and
              // capture dies with the node it was taken on — dropping the
              // `pointerup` that ends the hold.
              const capture = el.node || e.target
              if (capture && capture.setPointerCapture && e.pointerId !== undefined) {
                try {
                  capture.setPointerCapture(e.pointerId)
                } catch (_) {}
              }
              // Announce this composer as the voice host BEFORE the engine
              // opens — root.voiceHost contract (shared/components/
              // VoiceStrip.js): the in-place strip renders here instead of the
              // full-screen overlay. UI-only root write → preventFetch.
              if (s.root && s.root.update) {
                s.root.update({ voiceHost: 'chat' }, { preventFetch: true })
              }
              el.call('voicePttPress', { mode: 'talk', scope: el.scope.chatScopeOf(el) })
            },
            onPointerup: (e, el) => {
              if (e.button !== 0) return
              el.call('voicePttRelease', { mode: 'talk', scope: el.scope.chatScopeOf(el) })
            },
            onPointercancel: (e, el) => el.call('voicePttCancel')
          }
          // No AI-settings button in this row (Nika 2026-09-26, verbatim:
          // "remove AI settings button from ai sidebar textarea"). The one
          // entry is the header's SettingsBtn (Actions, above) — the same
          // AiSettingsButton, opening the same SettingsDrawer (target
          // switching + AI params).
        },
        SendButton: {
          extends: 'AssistantSendButton',
          aria: { label: (el) => el.call('polyglot', 'ai.chat.sendMessage') },
          title: (el) => el.call('polyglot', 'ai.chat.sendMessage'),
          isReadyDeps: ['draft', (el, s) => s.root && s.root.bridgeStatus],
          isReady: (el, s) => !el.scope.isComposerSendDisabled(el, s),
          // The glyph is the send arrow in every state. A turn in flight swaps
          // it for the house busy indicator (shared LoadingSpinner, its use #1:
          // inside a control while its action runs) — the sprite has no
          // `loading` glyph, so that name painted `#noIcon`, a hatched square
          // (measured live on dev, 2026-10-18).
          icon: 'arrowUp',
          Icon: {
            hideDeps: [(el, s) => s.root && s.root[el.scope.ck(el, 'chatTyping')]],
            hide: (el, s) => !!(s.root && s.root[el.scope.ck(el, 'chatTyping')])
          },
          LoadingSpinner: {
            hideDeps: [(el, s) => s.root && s.root[el.scope.ck(el, 'chatTyping')]],
            hide: (el, s) => !(s.root && s.root[el.scope.ck(el, 'chatTyping')])
          },
          opacity: (el, s) => (s.root && s.root[el.scope.ck(el, 'chatTyping')] ? '.5' : '1'),
          pointerEvents: (el, s) =>
            s.root &&
            (s.root[el.scope.ck(el, 'chatTyping')] || el.scope.isComposerSendDisabled(el, s))
              ? 'none'
              : 'auto',
          disabled: (el, s) => el.scope.isComposerSendDisabled(el, s),
          onClick: (e, el, s) => {
            e.preventDefault()
            el.scope.send(el, s)
          }
        }
      },

      // T18 — the in-place voice surface. Replaces the text trio (Textarea +
      // ComposerBar, hidden above/below) inside the same
      // ComposerWrap form shell while this composer hosts voice. Visibility
      // lives on the strip's own reactive `display:` predicate
      // (VoiceStrip.js).
      VoiceStrip: {
        hostKey: 'chat',
        // This box clips its overflow (the brand prompt box): the call's
        // words stay in the strip's row, not as bubbles above it.
        floatWords: false
      }
    },

    // Stop the native <form> submit from reloading the panel.
    onSubmit: (e) => {
      e.preventDefault()
    }
  },

  // Settings overlay — mounted ON Chat (not the host page) so every consumer
  // (standalone simone, chrome-connect panel, ext-ui, workspace drawer) gets
  // the same settings surface from the SettingsBtn without wiring anything.
  Settings: {
    if: (el, s) => !!s.root.showSettings,
    extends: 'SettingsDrawer'
  }
}

// el.node.scrollTop / ResizeObserver in Messages.onMounted/onCreate/onUpdate
// are a known DOMQL escape hatch — scroll-to-bottom on new content has no
// canonical DOMQL prop yet. Same recipe as `chat/components/MessageList.js`
// (ChatMessageList.onMounted/onCreate) — see AIUX-SCROLL (tickets/sonnet.md).
